
import React, { useState, useEffect, useCallback, useRef } from "react";
import apiClient from "../../api_client";
import debounce from "lodash/debounce";
import {
  Table,
  Checkbox,
  message,
  Card,
  Row,
  Col,
  Input,
  Button,
  Spin,
  Tooltip,
} from "antd";
import { API_URL } from "../../url_config";
import { NavigationStepsTransdev } from "../../components/navbars/navbar_transdev";
import { EditOutlined, ReloadOutlined } from "@ant-design/icons";
import ModifyItemPage from "../../components/modify_page";
import CoquillesTable from "../../components/counter";
import UserHeader from "../../components/user_header";

const { TextArea } = Input;

const columnTitles = {
  ligne: "Ligne",
  coquille: "Coquille à vérifier",
  test_ad_premier_sortant: "Test radio TETRA",
  remontee_de_ad_sae_tr: "Remontée AD SAE-TR",
  geolocalisation: "Géolocalisation AD SAE",
  gestion_ad: "Acquittement / Clôture SAE",
  remontee_de_ad_tetra: "Remontée AD TETRA",
  ecoute_d_ambiance: "Écoute d’ambiance",
  prise_en_compte_appel: "Appel individuel",
  signal: "ÉTAT",
};

const tableStyle = document.createElement("style");
tableStyle.innerHTML = `
  .custom-row td {
    border-bottom: 2px solid #d9d9d9 !important;
  }
  .ant-table-thead > tr > th {
    background-color: #f5f5f5 !important;
    font-weight: 600;
    border-bottom: 2px solid #bfbfbf !important;
    color: #333;
  }
  .ant-table-tbody > tr:hover > td {
    background-color: #f0f0f0 !important;
  }
  .ant-table {
    border-collapse: separate !important;
    border-spacing: 0;
  }
`;
document.head.appendChild(tableStyle);

const EditableTextArea = ({ recordId, field, initialValue, onPatch }) => {
  const [value, setValue] = useState(initialValue || "");
  useEffect(() => setValue(initialValue || ""), [initialValue]);
  const debouncedSave = useCallback(
    debounce((val) => onPatch(recordId, field, val), 800),
    [recordId, field, onPatch]
  );
  const handleChange = (e) => {
    const val = e.target.value;
    setValue(val);
    debouncedSave(val);
  };
  return (
    <TextArea
      rows={5}
      value={value}
      onChange={handleChange}
      placeholder="Saisissez une remarque..."
    />
  );
};

const EditableInput = ({ recordId, field, initialValue, onPatch }) => {
  const [value, setValue] = useState(initialValue || "");
  useEffect(() => setValue(initialValue || ""), [initialValue]);
  const debouncedSave = useCallback(
    debounce((val) => onPatch(recordId, field, val), 800),
    [recordId, field, onPatch]
  );
  const handleChange = (e) => {
    const val = e.target.value;
    setValue(val);
    debouncedSave(val);
  };
  return (
    <Input
      value={value}
      onChange={handleChange}
      placeholder="Saisir une coquille..."
      style={{ width: "100%", borderRadius: "6px" }}
    />
  );
};

function TableGeolocalisation({ record, onPatch }) {
  return (
    <div style={{ padding: "20px", marginTop: "40px" }}>
      <Row gutter={16}>
        <Col span={12}>
          <Card title="Commentaire" bordered>
            <EditableTextArea
              recordId={record.id}
              field="comment"
              initialValue={record.comment}
              onPatch={onPatch}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Informations (Supervision)" bordered>
            <EditableTextArea
              recordId={record.id}
              field="aide"
              initialValue={record.aide}
              onPatch={onPatch}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

const ValidationGeolocalisationTransdev = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchText, setSearchText] = useState("");
  const pendingUpdatesRef = useRef(new Map());
  const timeoutRefs = useRef(new Map());

  const fetchData = async () => {
    try {
      const res = await apiClient.get(
        `${API_URL}/validations/validation_geolocalisation/?ot_tag=transdev`
      );
      const formatted = res.data.map((item, index) => ({
        key: item.id || index,
        id: item.id,
        ligne: item.ligne,
        coquille: item.coquille,
        test_ad_premier_sortant: item.test_ad_premier_sortant,
        remontee_de_ad_sae_tr: item.remontee_de_ad_sae_tr,
        geolocalisation: item.geolocalisation,
        gestion_ad: item.gestion_ad,
        remontee_de_ad_tetra: item.remontee_de_ad_tetra,
        ecoute_d_ambiance: item.ecoute_d_ambiance,
        prise_en_compte_appel: item.prise_en_compte_appel,
        comment: item.comment,
        aide: item.aide,
        signal: item.signal,
      }));
      setData(formatted);
    } catch (error) {
      console.error(error);
      message.error("Erreur de chargement des données");
    }
  };

  const handleManualRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  useEffect(() => {
    setLoading(true);
    fetchData().finally(() => setLoading(false));
  }, []);

  const sendUpdate = async (updateKey) => {
    const update = pendingUpdatesRef.current.get(updateKey);
    if (!update) return;
    try {
      await apiClient.patch(
        `${API_URL}/validations/validation_geolocalisation/${update.id}/`,
        { [update.field]: update.value }
      );
      const current = pendingUpdatesRef.current.get(updateKey);
      if (current && current.timestamp === update.timestamp) {
        pendingUpdatesRef.current.delete(updateKey);
      }
    } catch (error) {
      console.error(error);
      message.error("Erreur lors de la mise à jour");
    }
  };

  const handleLocalChange = (recordId, field, value) => {
    setData((prev) =>
      prev.map((item) =>
        item.id === recordId ? { ...item, [field]: value } : item
      )
    );
    const updateKey = `${recordId}_${field}`;
    const existingTimeout = timeoutRefs.current.get(updateKey);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }
    pendingUpdatesRef.current.set(updateKey, { 
      id: recordId, 
      field, 
      value,
      timestamp: Date.now()
    });
    const timeoutId = setTimeout(() => {
      sendUpdate(updateKey);
      timeoutRefs.current.delete(updateKey);
    }, 1000);
    timeoutRefs.current.set(updateKey, timeoutId);
  };

  const filteredData = data.filter((item) => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      (item.ligne?.toString().toLowerCase() || "").includes(search) ||
      (item.coquille?.toString().toLowerCase() || "").includes(search) ||
      (item.comment?.toString().toLowerCase() || "").includes(search) ||
      (item.aide?.toString().toLowerCase() || "").includes(search)
    );
  });

  const columns = [
    { 
      title: columnTitles.ligne, 
      dataIndex: "ligne",
      sorter: (a, b) => (a.ligne || "").localeCompare(b.ligne || ""),
    },
    {
      title: columnTitles.coquille,
      dataIndex: "coquille",
      sorter: (a, b) => (a.coquille || "").localeCompare(b.coquille || ""),
      render: (val, record) => (
        <EditableInput
          recordId={record.id}
          field="coquille"
          initialValue={val}
          onPatch={handleLocalChange}
        />
      ),
    },
    ...[
      "test_ad_premier_sortant",
      "remontee_de_ad_sae_tr",
      "geolocalisation",
      "gestion_ad",
    ].map((field) => ({
      title: columnTitles[field],
      dataIndex: field,
      sorter: (a, b) => (a[field] === b[field] ? 0 : a[field] ? -1 : 1),
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) => handleLocalChange(record.id, field, e.target.checked)}
          className="custom-checkbox-border"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.2)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    })),
    {
      title: columnTitles.signal,
      dataIndex: "signal",
      sorter: (a, b) => a.signal - b.signal,
      render: (val, record) => {
        const color = val === 1 ? "#4CAF50" : val === -1 ? "#FF4D4F" : "#BFBFBF";
        const symbol = val === 1 ? "✓" : val === -1 ? "✗" : " ";
        const handleClick = () => {
          const nextVal = val === 0 ? 1 : val === 1 ? -1 : 0;
          handleLocalChange(record.id, "signal", nextVal);
        };
        return (
          <div onClick={handleClick} style={{ cursor: "pointer", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <div style={{
              width: 28,
              height: 28,
              borderRadius: "6px",
              backgroundColor: color,
              color: "white",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontWeight: "bold",
              fontSize: "16px",
              userSelect: "none",
              transition: "all 0.3s ease",
            }}>
              {symbol}
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <UserHeader />
      <div style={{ padding: "20px", position: "relative", paddingTop: "30px" }}>
        {loading && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(255,255,255,0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <Spin size="large" tip="Chargement..." />
          </div>
        )}
        <NavigationStepsTransdev selectedKey="geolocalisation" />
        <div style={{padding: "24px 0"}}>
          <CoquillesTable
            url={`${API_URL}/validations/counter/geolocalisation/?ot_tag=transdev`}
            labelAll="Nb lignes DSP"
            labelOk="Nb lignes OK"
            labelNok="Nb lignes KO"
          />
        </div>
        <Input
          placeholder="Rechercher dans toutes les colonnes..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{
            marginBottom: "16px",
            width: "100%",
            maxWidth: "500px"
          }}
          allowClear
        />
        <Tooltip title="Rafraîchir les données">
          <Button
            type="primary"
            shape="circle"
            size="large"
            icon={<ReloadOutlined style={{ fontSize: "20px" }} />}
            onClick={handleManualRefresh}
            loading={refreshing}
            className="app-refresh-button"
            style={{
              zIndex: 9999,
              boxShadow: "0 12px 32px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.15)",
              background: "linear-gradient(135deg, #40a9ff 0%, #1890ff 100%)",
              border: "none",
              width: "56px",
              height: "56px"
            }}
          />
        </Tooltip>
        <Table
          columns={columns}
          dataSource={filteredData}
          bordered
          style={{
            border: "2px solid #bfbfbf",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            marginTop: 60,
          }}
          rowClassName={() => "custom-row"}
          pagination={false}
          expandable={{
            expandedRowRender: (record) => (
              <TableGeolocalisation record={record} onPatch={handleLocalChange} />
            ),
          }}
        />
        <div style={{ padding: 20 }}>
          <ModifyItemPage
            apiUrl={`${API_URL}/validations/validation_geolocalisation/`}
            ot_tag="transdev"
            title="Gestion"
            rowKey="id"
            trigger={<Button icon={<EditOutlined />} shape="circle" />}
            columns={[{ title: columnTitles.ligne, dataIndex: "ligne", key: "ligne" }]}
            formFields={[{ name: "ligne", label: columnTitles.ligne, placeholder: "Entrez la ligne" }]}
            onRefresh={fetchData}
          />
        </div>
      </div>
    </>
  );
};

export default ValidationGeolocalisationTransdev;
